<script setup lang="ts">
import { computed } from "vue";
import { formatNumber } from "chartjs-graph";

const {
	deltaAbsolute,
	deltaRelative,
	showAbsolute = false,
	showRelative = false,
	format = "default",
} = defineProps<{
	deltaAbsolute: number;
	deltaRelative: number;
	showAbsolute?: boolean;
	showRelative?: boolean;
	format?: App.Enums.ValueFormat;
}>();

const direction = computed(() => Math.sign(deltaAbsolute) || 0);

const color = computed<"success" | "error" | "secondary-tonal">(() => {
	if (direction.value < 0) return "error";
	if (direction.value > 0) return "success";

	return "secondary-tonal";
});

const icon = computed(() => {
	if (direction.value < 0) return "mdi-chevron-down";
	if (direction.value > 0) return "mdi-chevron-up";

	return "mdi-minus";
});

const absoluteLabel = computed(() =>
	formatNumber(isNaN(deltaAbsolute) ? 0 : Math.abs(deltaAbsolute), format)
);

const relativeLabel = computed(() => {
	const value = isNaN(deltaRelative) ? 0 : deltaRelative;
	const disagreesWithIcon =
		Math.sign(value) !== 0 && Math.sign(value) !== direction.value;

	return formatNumber(
		disagreesWithIcon ? value : Math.abs(value),
		"percentage"
	);
});
</script>

<template>
	<v-chip
		class="delta-chip number-mono"
		:class="{
			'delta-chip--dual': showAbsolute && showRelative,
		}"
		:color="color"
	>
		<v-icon size="x-small" :icon="icon" />
		<span v-if="showAbsolute">{{ absoluteLabel }}</span>
		<v-divider
			v-if="showAbsolute && showRelative"
			vertical
			class="border-opacity-50"
		/>
		<span v-if="showRelative">
			{{ relativeLabel }}
		</span>
	</v-chip>
</template>

<style scoped lang="scss">
.v-chip.delta-chip {
	display: inline-flex;
	max-width: 105px;
	height: 14px;
	padding: 0 4px 0 2px;
	font-family: "Roboto", sans-serif;
	font-size: 10px;
	letter-spacing: normal;

	:deep(.v-chip__content) {
		gap: 2px;
	}

	:deep(.v-divider--vertical) {
		align-self: center;
		height: 1em;
		max-height: none;
		margin: 0;
	}
}

.v-chip.delta-chip--dual {
	max-width: 150px;
}
</style>
